/**
 * Language Context
 * Provides multi-language support throughout the application
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { LANGUAGES, DEFAULT_LANGUAGE, t as translate, getTranslations } from '../lib/i18n';

const LanguageContext = createContext(null);

const STORAGE_KEY = 'xm-minotauro-language';

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    // Try to get from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && LANGUAGES[saved]) {
        return saved;
      }
      // Try to detect from browser
      const browserLang = navigator.language?.split('-')[0];
      if (browserLang && LANGUAGES[browserLang]) {
        return browserLang;
      }
    }
    return DEFAULT_LANGUAGE;
  });

  const [translations, setTranslations] = useState(() => getTranslations(language));

  // Update translations when language changes
  useEffect(() => {
    setTranslations(getTranslations(language));
  }, [language]);

  // Persist language preference
  const setLanguage = useCallback((lang) => {
    if (LANGUAGES[lang]) {
      setLanguageState(lang);
      localStorage.setItem(STORAGE_KEY, lang);
      // Update document lang attribute for accessibility
      document.documentElement.lang = lang;
    }
  }, []);

  // Translation function with current language.
  //
  // El segundo argumento es el TEXTO POR DEFECTO, no el idioma. Antes se
  // ignoraba: `t('auth.changePasswordTitle', 'Change your password')` acababa
  // llamando a translate(key, 'Change your password'), no encontraba la clave y
  // pintaba la clave cruda en pantalla (la vieron todos los usuarios nuevos en
  // el cambio de contraseña obligatorio). Ahora, si la clave no existe, se
  // devuelve el texto por defecto — un fallo así no vuelve a verse en pantalla.
  const t = useCallback((key, defaultValue) => {
    const value = translate(key, language);
    if (value === key && typeof defaultValue === 'string' && defaultValue.length > 0) {
      return defaultValue;
    }
    return value;
  }, [language]);

  // Get available languages as array
  const getAvailableLanguages = useCallback(() => {
    return Object.values(LANGUAGES);
  }, []);

  const value = {
    language,
    setLanguage,
    languages: LANGUAGES,
    t,
    translations,
    getAvailableLanguages
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook to use language context
 * @returns {{ language: string, setLanguage: function, languages: object, t: function, translations: object }}
 */
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

/**
 * Hook to just get the translation function
 * More lightweight than useLanguage
 */
export function useTranslation() {
  const { t, language } = useLanguage();
  return { t, language };
}

export default LanguageContext;
